const adhocSchema = {
  type: 'object',
  properties: {
    name: { type: 'string' },
    value: {},
  },

  required: ['name', 'value'],
};

/**
 * Adhoc Identity Set
 * @typedef AdhocIdentityDocument
 * @type {object}
 * @property {string} name
 * @property {any} value
 */

/**
 * Standard Identity Set
 * @typedef IdentityDocument
 * @type {object}
 * @property {number} callsLeadId Lead primary key from the legacy calls database
 * @property {number} callsApplicationId Application primary key from the legacy calls database
 * @property {number} callsCustomerId Customer primary key from the legacy calls database
 * @property {string} crmActivityGuid CRM activity GUID (unused)
 * @property {string} crmAccountGuid CRM account GUID (unused)
 * @property {string} crmContactGuid CRM contact GUID (unused)
 * @property {string} crmCallerGuid CRM caller GUID (unused)
 * @property {string} crmCampaignGuid CRM campaign GUID (unused)
 * @property {number} tontoApplicationId Application primary key from the legacy tonto_v4 database
 * @property {number} tontoApplicantId Borrower primary key from the legacy tonto_v4 database
 * @property {number} justiceApplicationId Application primary key from the legacy BureauServices database
 * @property {number} justiceApplicantId ApplicationPartyRole primary key from the legacy BureauServices database
 * @property {number} justiceSecurityId Security primary key from the legacy BureauServices database
 * @property {number} justiceLoanId Loan primary key from the legacy BureauServices database
 * @property {number} bureauApplicationId Application primary key from the legacy BureauServices database
 * @property {string} k2Serial K2 workflow serial number
 * @property {number} v8ApplicationId Application primary key from the legacy v8 database
 * @property {number} v8ApplicantId Applicant primary key from the legacy v8 database
 * @property {number} v8Security Security primary key from the legacy v8 database
 * @property {number} crumbsPartyId Party primary key from the Crumbs database
 * @property {number} crumbsPartyRoleId PartyRole primary key from the Crumbs database
 * @property {number} crumbsLoginId Login primary key from the Crumbs database
 * @property {number} partyBankAccountId PartyBankAccount primary key from the Crumbs database
 * @property {number} crumbsAddressId Address primary key from the Crumbs database
 * @property {number} bankAccountId BankAccount primary key from the Crumbs database
 * @property {string} vxApplicationId VX application ID (8xxxxxxx)
 * @property {string} vxApplicantId VX member _id
 * @property {string} vxSecurityId VX security _id
 * @property {string} vxPurposeId VX purpose _id
 * @property {string} vxSplitId VX split _id
 * @property {string} vxDisbursementId VX disbursement _id
 * @property {number} ultracsClientId Ultracs client number
 * @property {string} ultracsXref Ultracs xref
 * @property {string} ultracsAccount Ultracs account number
 * @property {string} ultracsClient Ultracs client name
 * @property {string} traxJob Job primary key from the Trax database
 * @property {number} traxDepartmentId Department primary key from the Trax database
 * @property {number} traxUserId User primary key from the Trax database
 * @property {number} traxJobTypeId JobType primary key from the Trax database
 * @property {number} traxJobTypeQueryId JobTypeQuery primary key from the Trax database
 * @property {string} formRequestId Form request ID
 * @property {number} salesLeadId Lead primary key from the Sales database
 * @property {string} salesPurposeId Sales purpose _id
 * @property {string} salesCustomerId Sales customer _id
 * @property {string} settlementId Settlement _id
 * @property {string} settlementProcessHistoryId SettlementProcessHistory _id
 * @property {string} settlementPurposeId SettlementPurpose _id
 * @property {string} settlementDisbursementId SettlementDisbursement _id
 * @property {string} hiveQuoteId Hive quote id
 * @property {string} typeSourceId Types source _id
 * @property {string} typeGetterId Types getter _id
 * @property {number} workbookId Workbook primary key from the workbooks database
 * @property {string} reactionId Reaction _id from the reactor database
 * @property {string[]} recipients Array of recipients
 * @property {string} expenseClaimSerial Serial number for expense claim forms in trax
 * @property {string} paymentRequestSerial Serial number for payment request forms in trax
 * @property {string} purchaseOrderSerial Serial number for purchase order forms in trax
 * @property {string} refundRequestSerial Serial number for refund request forms in trax
 * @property {string} complaintInvestigationSerial Serial number for complaint investigation forms in trax
 * @property {string} productManagerRequestSerial Serial number for product manager request forms in trax
 * @property {string} retentionRateRequestSerial Serial number for retention rate request forms in trax
 * @property {string} fitsRequestNumber FITS request number (unused)
 * @property {string} documentOrderId DocumentOrder primary key
 * @property {string} documentOrderRecipientId DocumentOrderRecipient primary key
 * @property {number} collateralId Collateral primary key from the collateral database
 * @property {number} conditionId Condition primary key from the conditions database
 * @property {string} insuranceId Insurance _id
 * @property {string} outgoingId Outgoing _id
 * @property {string} liveChatId ID of a live chat session
 * @property {AdhocIdentityDocument[]} adhoc A list of informational-only identities
 */
module.exports = {
  id: '/identities',
  type: 'object',
  properties: {
    /* Legacy retail */
    callsLeadId: { type: 'integer' },
    callsApplicationId: { type: 'integer' },
    callsCustomerId: { type: 'integer' },

    /* CRM identifiers */
    crmActivityGuid: { type: 'string', format: 'uuid' },
    crmAccountGuid: { type: 'string', format: 'uuid' },
    crmContactGuid: { type: 'string', format: 'uuid' },
    crmCallerGuid: { type: 'string', format: 'uuid' },
    crmCampaignGuid: { type: 'string', format: 'uuid' },

    /* Tonto */
    tontoApplicationId: { type: 'integer' },
    tontoApplicantId: { type: 'integer' },

    /* Justice */
    justiceApplicationId: { type: 'integer' },
    justiceApplicantId: { type: 'integer' },
    justiceSecurityId: { type: 'integer' },
    justiceLoanId: { type: 'integer' },
    bureauApplicationId: { type: 'integer' },
    k2Serial: { type: 'string' },

    /* v8 */
    v8ApplicationId: { type: 'integer' },
    v8ApplicantId: { type: 'integer' },
    v8Security: { type: 'integer' },

    /* Crumbs */
    crumbsPartyId: { type: 'integer' },
    crumbsPartyRoleId: { type: 'integer' },
    crumbsLoginId: { type: 'integer' },
    partyBankAccountId: { type: 'integer' },
    bankAccountId: { type: 'integer' },
    crumbsAddressId: { type: 'integer' },

    /* Vx */
    vxApplicationId: { type: 'string', pattern: '^8\\d{7}$' },
    vxApplicantId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    vxSecurityId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    vxPurposeId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    vxSplitId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    vxDisbursementId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    /* Ultracs */
    ultracsClientId: { type: 'integer' },
    ultracsXref: { type: 'string' },
    ultracsAccount: { type: 'string' },
    ultracsClient: { type: 'string' },

    /* Trax */
    traxJob: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    traxDepartmentId: { type: 'integer' },
    traxUserId: { type: 'integer' },
    traxJobTypeId: { type: 'integer' },
    traxJobTypeQueryId: { type: 'integer' },

    /* Trax Form */
    formRequestId: { type: 'string' },

    /* Sales */
    salesLeadId: { type: 'integer' },
    salesPurposeId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    salesCustomerId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    /* Settlements */
    settlementId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    settlementProcessHistoryId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    settlementPurposeId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    settlementDisbursementId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    /* Hive/Georgie */
    hiveQuoteId: { type: 'string' },

    /* FAAC */
    typeSourceId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    typeGetterId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    workbookId: { type: 'integer' },
    reactionId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    /* ???? */
    recipients: {
      type: 'array',
      items: {
        type: 'string',
      },
    },

    expenseClaimSerial: { type: 'string' },
    paymentRequestSerial: { type: 'string' },
    purchaseOrderSerial: { type: 'string' },
    refundRequestSerial: { type: 'string' },
    complaintInvestigationSerial: { type: 'string' },
    productManagerRequestSerial: { type: 'string' },
    retentionRateRequestSerial: { type: 'string' },
    fitsRequestNumber: { type: 'string' },

    documentOrderId: { type: 'string', pattern: '^[a-f\\d]{24}$' },
    documentOrderRecipientId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    collateralId: { type: 'integer' },

    conditionId: { type: 'integer' },

    insuranceId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    outgoingId: { type: 'string', pattern: '^[a-f\\d]{24}$' },

    liveChatId: { type: 'string' },

    adhoc: {
      type: 'array',
      items: adhocSchema,
    },
  },
};
